import React, { useState, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import { B } from '../utils/constants';
import { Menu, X } from 'lucide-react';

function Layout({ activePage, onNavigate, user, onLogout, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const isPresentationMode = searchParams.get('present') === 'true';

  // Presentation mode - full screen without sidebar
  if (isPresentationMode) {
    return (
      <div className="min-h-screen p-4" style={{ backgroundColor: B.cream, fontFamily: 'Georgia, serif' }}>
        {children}
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: B.cream, fontFamily: 'Georgia, serif' }}>
      {/* Mobile/Tablet Header with Hamburger - shows below 1024px */}
      <div
        className="xl:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3"
        style={{ backgroundColor: B.forest }}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
        >
          <Menu className="w-6 h-6 text-white" />
        </button>
        <div className="text-white">
          <span className="text-sm font-semibold tracking-widest" style={{ fontFamily: 'Gill Sans, sans-serif' }}>
            TECOVAS
          </span>
          <span className="text-xs ml-2" style={{ color: B.sage }}>SUPPLY CHAIN EXECUTION LAYER</span>
        </div>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Mobile/Tablet Overlay */}
      {sidebarOpen && (
        <div
          className="xl:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop (xl+): always visible and pinned to the viewport (sticky,
          h-screen) so a rail taller than the window scrolls inside its own nav instead
          of scrolling the whole document and dragging page frames (the QBR iframe)
          up out of view. Tablet/Mobile: slide in. */}
      <div className={`
        fixed xl:sticky xl:top-0 xl:h-screen xl:shrink-0 inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full xl:translate-x-0'}
      `}>
        <Sidebar
          activePage={activePage}
          onNavigate={(page) => {
            onNavigate(page);
            setSidebarOpen(false); // Close sidebar on navigation (mobile)
          }}
          user={user}
          onLogout={onLogout}
          onClose={() => setSidebarOpen(false)}
          isMobile={true}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 pt-16 xl:pt-0 p-4 overflow-x-hidden overflow-y-auto overscroll-contain h-screen" id="main-content">
        {children}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          /* Force all colors to print */
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Hide sidebar and mobile header completely */
          .fixed,
          .xl\\:hidden,
          [class*="sidebar"],
          [class*="Sidebar"],
          .z-40,
          .z-50 {
            display: none !important;
            visibility: hidden !important;
            width: 0 !important;
            height: 0 !important;
          }

          /* Remove ALL height and overflow constraints */
          html, body, #root {
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: visible !important;
          }

          /* Target layout wrapper */
          .flex.min-h-screen {
            display: block !important;
            height: auto !important;
          }

          /* Target main content by ID */
          #main-content {
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
            padding: 0.5in !important;
            padding-top: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
            position: static !important;
          }

          /* Override Tailwind utilities */
          .h-screen {
            height: auto !important;
          }

          .overflow-y-auto,
          .overflow-x-hidden,
          .overflow-hidden {
            overflow: visible !important;
          }

          .pt-16 {
            padding-top: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default memo(Layout);
